import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Card,
  Empty,
  Input,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { GuardianDirectoryRow } from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin surface — Guardians directory.
 * Derived view: every distinct guardian across student_guardian, with the wards
 * they parent. Same phone can appear for two students (sibling detection); we
 * still show both rows so you can act per-parent.
 */
export function GuardiansListPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['guardians', institution, q],
    queryFn: () => productApi.listGuardians(institution, q || undefined),
    enabled: !!institution,
  });

  const total = list.data?.total ?? 0;
  const primaryCount = (list.data?.rows ?? []).filter((r) => r.wards.some((w) => w.is_primary)).length;
  const siblingCount = (list.data?.rows ?? []).filter((r) => r.wards.length > 1).length;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Guardians</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          The adult directory — derived from every student's linked guardians.
        </Typography.Text>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="On file" value={total} /></Card>
        <Card size="small"><Statistic title="Primary payers" value={primaryCount} /></Card>
        <Card size="small"><Statistic title="Sibling parents" value={siblingCount} suffix={<Typography.Text type="secondary" style={{ fontSize: 11 }}>2+ wards</Typography.Text>} /></Card>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Input
          size="small"
          placeholder="Name or phone"
          prefix={<SearchOutlined style={{ color: '#8a94a6' }} />}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          allowClear
          style={{ maxWidth: 320 }}
        />
      </div>

      <Card size="small" title={total === 0 ? 'No guardians' : `${list.data?.rows.length ?? 0} shown`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
        {list.isError && <Alert type="error" showIcon message="Could not load guardians" description={String(list.error)} />}
        {list.data && list.data.rows.length === 0 && (
          <Empty
            style={{ padding: '20px 0' }}
            description={
              <span>
                {total === 0
                  ? 'No guardians yet — add students with guardians first.'
                  : 'Nothing matches the filter.'}
              </span>
            }
          />
        )}
        {list.data && list.data.rows.length > 0 && (
          <Table<GuardianDirectoryRow>
            size="small"
            dataSource={list.data.rows}
            rowKey="id"
            pagination={false}
            onRow={(record) => ({
              onClick: (e) => {
                // don't hijack ward-tag clicks
                if ((e.target as HTMLElement).closest('.ant-tag')) return;
                navigate(`/i/${institution}/admin/guardians/${record.id}`);
              },
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                key: 'photo',
                width: 40,
                render: (_, r) => (
                  <Avatar size={28} style={{ background: `hsl(${(r.name.length * 51) % 360}, 40%, 60%)`, fontSize: 11 }}>
                    {r.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                  </Avatar>
                ),
              },
              { title: 'Name', dataIndex: 'name', key: 'name', render: (v: string) => <span style={{ fontWeight: 500, color: '#14213d' }}>{v}</span> },
              { title: 'Phone', dataIndex: 'phone', key: 'phone', width: 140, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Email', dataIndex: 'email', key: 'email', render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
              {
                title: 'Wards',
                key: 'wards',
                render: (_, r) => (
                  <Space size={4} wrap>
                    {r.wards.map((w) => (
                      <Tag
                        key={w.student_id}
                        color={w.is_primary ? 'success' : undefined}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/i/${institution}/admin/students/${w.admission_number}`)}
                      >
                        {w.student_name}
                        <span style={{ color: '#8a94a6', marginLeft: 4 }}>
                          · {w.grade}{w.section ? `-${w.section}` : ''} · {w.relation}
                        </span>
                      </Tag>
                    ))}
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}
